import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { environment } from '@env/environment';
import { CartAdjustments, GroupAdjustments } from '@app/cart/services/voucher-adjustments/data';
import { VoucherAdjustmentsService } from '@app/cart/services/voucher-adjustments/voucher-adjustments.service';
import { select, Store } from '@ngrx/store';
import * as fromApp from '@app/store/app.reducers';
import * as fromCart from '@app/store/cart/cart.reducers';

enum ValidationResultMessageCode {
    'Used' = 'USED',
    'InvalidOrExpired' = 'INVALID_OR_EXPIRED'
}

interface ValidationResultMessageRest {
    code: ValidationResultMessageCode;
    message: string;
}

interface ValidationResultRest {
    valid: boolean;
    messages: ValidationResultMessageRest[];
}


export interface PromoCodeValidationResult {
    valid: boolean;
    message: string;
    code?: string;
}

interface ValidationResult {
    [key: string]: ValidationResultRest;
}

export const valid: PromoCodeValidationResult = {
    valid: true,
    message: ''
};

export const invalid: PromoCodeValidationResult = {
    valid: false,
    message: 'This voucher code is invalid or expired.',
    code: ValidationResultMessageCode.InvalidOrExpired
};

export const redeemed: PromoCodeValidationResult = {
    valid: false,
    message: 'This voucher code has already been used',
    code: ValidationResultMessageCode.Used
};

export const recentlyRedeemed: PromoCodeValidationResult = {
    valid: false,
    message: 'We\'re sorry, this voucher has recently been redeemed by another order',
    code: ValidationResultMessageCode.Used
};

export const empty: PromoCodeValidationResult = {
    valid: false,
    message: 'Please provide a promo code.'
};

const error: PromoCodeValidationResult = {
    valid: false,
    message: 'Something went wrong. Please try again later.'
};

const toPromoCodeValidationResult = code => (result: ValidationResult): PromoCodeValidationResult => {
    if (!result[code]) {
        return invalid;
    }

    const validationResult: ValidationResultRest = result[code];

    if (validationResult.valid) {
        return valid;
    }
    const [message] = validationResult.messages;
    const validationCode = message.code;
    return validationCode ===  ValidationResultMessageCode.Used ? redeemed : invalid;
};

const noApplicableItemsError: PromoCodeValidationResult = {
    valid: false,
    message: 'The promo code you entered does not apply to any items in your cart. Please try again.'
};

@Injectable({
    providedIn: 'root'
})
export class PromoCodeService {

    constructor(
        private http: HttpClient,
        private voucherService: VoucherAdjustmentsService,
        private store: Store<fromApp.AppState>) {
    }

    validate(code: string): Observable<PromoCodeValidationResult> {
        return !code ? of(empty) : this.validateWithAdjustments(code);
    }

    placeOrderValidate(code: string): Observable<PromoCodeValidationResult> {
        if (!code) {
            return of(empty);
        } else {
            return this.validateWithAdjustments(code).pipe(map(this.toPlaceOrderMessage));
        }
    }

    validateOnServer(code): Observable<PromoCodeValidationResult> {
        const url = `${environment.csBaseURL}/cart/vouchers/validate`;
        const data = [code];
        const headers = new HttpHeaders({ 'content-type': 'application/json' });
        return this.http.post<ValidationResult>(url, data, { headers }).pipe(
            map(toPromoCodeValidationResult(code)),
            catchError(e => [this.toValidationResultFromError(e, code)])
        );
    }

    toValidationResultFromError(errorResponse, code: string): PromoCodeValidationResult {
        return errorResponse[code] !== null ? toPromoCodeValidationResult(code)(errorResponse) : error;
    }

    validateWithAdjustments(code: string): Observable<PromoCodeValidationResult> {
        const cartState$ = this.getCartState();
        const group$ = this.getBaseGroups();

        return this.validateOnServer(code).pipe(
            switchMap((result: PromoCodeValidationResult)  => {

                const { valid: isValid } = result;

                if (!isValid) {
                    return [result];
                }

                return this.voucherService.loadCartAdjustments(of(code), group$, cartState$).pipe(
                    map((adjustments) => this.validateSavings(isValid, code, adjustments)),
                    take(1)
                );
            }),
            take(1)
        );
    }

    resetAdjustmentIfCodeIsEmpty(code: string, id: string | number) {
        if (!code) {
            this.resetAdjustments(id);
        }
    }

    resetAdjustments(id: string | number) {
        this.voucherService.removeCartAdjustments(id);
    }

    validateSavings(isValidCode: boolean, code: string, adjustments: CartAdjustments): PromoCodeValidationResult  {
        if (!code) {
            return empty;
        }

        if (!this.hasApplicableItemAdjustment(adjustments)) {
            return noApplicableItemsError;
        }

        if (this.isCartAdjustmentInValidOrExpired(adjustments)) {
            return invalid;
        }

        const hasNoSavings = this.getHasNoSavings(code, adjustments);

        if (hasNoSavings && isValidCode) {
            return noApplicableItemsError;
        } else if (isValidCode) {
            return valid;
        } else {
            return invalid;
        }
    }

    private getBaseGroups() {
        return this.store.pipe(
            select(fromApp.getCartGroups),
            map(groups => groups.filter(group => group.items.length > 0)),
            take(1),
            shareReplay(1)
        );
    }

    private getCartState(): Observable<fromCart.State> {
        return this.store.pipe(
            select('cart'),
            filter<fromCart.State>(Boolean),
            take(1),
            shareReplay(1)
        );
    }

    private getSavings(adjustments: CartAdjustments): number {
        return -adjustments.adjustment;
    }

    private getHasNoSavings(code: string, adjustments: CartAdjustments) {
        const savings = this.getSavings(adjustments);
        return code !== '' && savings <= 0;
    }

    private isCartAdjustmentInValidOrExpired(adjustments: CartAdjustments): boolean {
    // return true if all applicable item adjustments are inactive/expired
        return !adjustments.groups.some(
            (group: GroupAdjustments) => group.items
                .filter(item => item.applicable)
                .some(item => !item.invalidExpired)
        );
    }

    // return true if theres an applicable item adjustments
    private hasApplicableItemAdjustment(adjustments: CartAdjustments): boolean {
        return adjustments.groups.some(
            (group: GroupAdjustments) => group.items.some(item => item.applicable)
        );
    }

    private toPlaceOrderMessage(result: PromoCodeValidationResult) {
        return result.code === ValidationResultMessageCode.Used ? recentlyRedeemed : result;
    }


}
